import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhoneContext = createContext(null);
export const usePhone = () => useContext(PhoneContext);

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(max-width: 768px)').matches || 'ontouchstart' in window);

export function PhoneProvider({ children }) {
  const { token, user } = useAuth();
  const [config, setConfig] = useState(null);
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [pendingNumber, setPendingNumber] = useState('');
  const loadedFor = useRef(null);

  useEffect(() => {
    if (!user || !token || loadedFor.current === user.id) return;
    loadedFor.current = user.id;
    axios.get(`${API}/phone/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setConfig(r.data))
      .catch(() => setConfig(null));
  }, [user, token]);

  const saveSettings = useCallback(async (patch) => {
    setConfig((c) => ({ ...c, ...patch }));
    try {
      await axios.put(`${API}/phone/settings`, patch, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* keep optimistic value */ }
  }, [token]);

  const deskphoneCall = useCallback(async (number, meta) => {
    try {
      await axios.post(`${API}/yabbit/click-to-call`,
        { destination: number, reason: meta?.reason, context: meta?.context },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Your phone is ringing — answer to connect to ${number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Click-to-call failed');
    }
  }, [token]);

  const dial = useCallback((rawNumber, meta) => {
    const number = String(rawNumber || '').replace(/[^\d+]/g, '');
    if (!number || !config?.phone_enabled) return;
    if (isMobileDevice()) {
      const mode = config?.mobile_mode || 'yabbit_app';
      if (mode === 'dialer') { window.location.href = `tel:${number}`; return; }
      if (mode === 'ask') {
        setPendingNumber(number);
        setWidgetOpen(true);
        return;
      }
      // yabbit_app: hand to the Yabbit mobile app, fall back to the dialer
      const t = Date.now();
      window.location.href = `yabbit://call?number=${encodeURIComponent(number)}`;
      setTimeout(() => { if (Date.now() - t < 1600) window.location.href = `tel:${number}`; }, 1200);
      return;
    }
    const mode = config?.call_mode || 'webrtc';
    if (mode === 'deskphone' && config?.deskphone_available) {
      deskphoneCall(number, meta);
      return;
    }
    setPendingNumber(number);
    setWidgetOpen(true);
  }, [config, deskphoneCall]);

  const value = {
    config, setConfig, saveSettings, dial, deskphoneCall,
    widgetOpen, setWidgetOpen, pendingNumber, setPendingNumber,
    enabled: !!user,
  };
  return <PhoneContext.Provider value={value}>{children}</PhoneContext.Provider>;
}
